/**
 * Phase 2 (Feb 2026) — Plain-german audit chronicle.
 *
 * "Was VIONA für dich erledigt hat" — human-readable list of the last
 * 20 audit events, translated from action codes into full sentences.
 * No jargon. Direct link to the full audit trail for advanced users.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { api } from "../../lib/api";
import EmptyState from "./EmptyState";

const PHRASES = {
  "campaigns.publish": (e) => "Kampagne veröffentlicht",
  "campaigns.stop_delivery": () => "Werbung gestoppt",
  "campaigns.paused": () => "Kampagne pausiert",
  "campaigns.resumed": () => "Kampagne wieder gestartet",
  "campaigns.budget_change": (e) =>
    `Tagesbudget aktualisiert${
      e?.after?.new_daily_eur != null ? ` auf ${e.after.new_daily_eur} €` : ""
    }`,
  "approvals.granted": () => "Freigabe erteilt",
  "approvals.rejected": () => "Freigabe abgelehnt",
  "approvals.requested": () => "Freigabe angefordert",
  "budget.created": () => "Budget angelegt",
  "budget.updated": () => "Budget geändert",
  "budget.ended": () => "Budget beendet",
  "autopilot.paused": (e) =>
    `Automatik pausiert${
      e?.after?.revoked_delegations_count
        ? ` · ${e.after.revoked_delegations_count} Delegationen widerrufen`
        : ""
    }`,
  "autopilot.action.executed": () => "Autopilot-Aktion ausgeführt",
  "autopilot.action.approved": () => "Autopilot-Aktion freigegeben",
  "autopilot.action.rejected": () => "Autopilot-Aktion abgelehnt",
  "orchestrator.run_started": () => "Auftrag gestartet",
  "orchestrator.run_cancelled": () => "Auftrag abgebrochen",
  "delegation.granted": () => "Delegation erteilt",
  "delegation.revoked": () => "Delegation widerrufen",
  "policy_guard.reconciled.confirmed_stopped": () =>
    "Anbieter-Bestätigung: Werbung gestoppt",
  "policy_guard.reconciled.provider_confirmed": () =>
    "Anbieter-Bestätigung erhalten",
  "policy_guard.reconciled.unclear": () => "Status beim Anbieter unklar",
};

function toSentence(e) {
  const fn = PHRASES[e.action];
  if (fn) return fn(e);
  return e.action.replace(/[._]/g, " ");
}

function formatWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return "gerade eben";
  if (mins < 60) return `vor ${mins} min`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `vor ${hrs} h`;
  return d.toLocaleDateString();
}

export default function VionaChronicle() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api
      .get("/audit/events", { params: { limit: 20 } })
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]));
  }, []);

  return (
    <Card data-testid="viona-chronicle">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between">
          Was VIONA für dich erledigt hat
          <Link
            to="/app/audit"
            className="text-xs font-normal underline text-muted-foreground hover:text-foreground"
            data-testid="viona-chronicle-view-all"
          >
            Vollständige Historie
          </Link>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {items == null ? (
          <p className="text-sm text-muted-foreground">Lade Chronik …</p>
        ) : items.length === 0 ? (
          <EmptyState
            title="Noch nichts passiert"
            description="Sobald du deine erste Kampagne startest, erscheint hier eine Klartext-Chronik."
            testid="viona-chronicle-empty"
          />
        ) : (
          <ol className="space-y-2">
            {items.slice(0, 10).map((e, i) => (
              <li
                key={e.id || i}
                data-testid={`viona-chronicle-item-${i}`}
                className="flex items-center justify-between text-sm border-b last:border-b-0 py-2"
              >
                <span>{toSentence(e)}</span>
                <span className="text-xs text-muted-foreground">
                  {formatWhen(e.created_at)}
                </span>
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}
